import type { NormalizedRoi } from "../pipeline/ScanPipeline.js";

/**
 * How a `<video>` with `object-fit: cover` maps its intrinsic pixel grid
 * onto its rendered CSS box. Needed in both directions: to turn the
 * on-screen viewfinder rectangle into a normalized ROI in *video* pixel
 * space (what the pipeline should crop and decode), and to turn decoded
 * symbol corner points (video pixel space) back into on-screen coordinates
 * for the AR overlay.
 */
export function coverTransform(
  videoWidth: number,
  videoHeight: number,
  boxWidth: number,
  boxHeight: number,
): { scale: number; offsetX: number; offsetY: number } {
  const videoAspect = videoWidth / videoHeight;
  const boxAspect = boxWidth / boxHeight;
  let scale: number;
  if (videoAspect > boxAspect) {
    scale = boxHeight / videoHeight;
  } else {
    scale = boxWidth / videoWidth;
  }
  const offsetX = (videoWidth * scale - boxWidth) / 2;
  const offsetY = (videoHeight * scale - boxHeight) / 2;
  return { scale, offsetX, offsetY };
}

export function videoPointToDisplay(
  point: { x: number; y: number },
  t: { scale: number; offsetX: number; offsetY: number },
): { x: number; y: number } {
  return { x: point.x * t.scale - t.offsetX, y: point.y * t.scale - t.offsetY };
}

export function displayRectToNormalizedRoi(
  rect: { x: number; y: number; width: number; height: number },
  videoWidth: number,
  videoHeight: number,
  boxWidth: number,
  boxHeight: number,
): NormalizedRoi {
  const t = coverTransform(videoWidth, videoHeight, boxWidth, boxHeight);
  const x = (rect.x + t.offsetX) / t.scale / videoWidth;
  const y = (rect.y + t.offsetY) / t.scale / videoHeight;
  const width = rect.width / t.scale / videoWidth;
  const height = rect.height / t.scale / videoHeight;
  return {
    x: clamp01(x),
    y: clamp01(y),
    width: clamp01(width, 1 - x),
    height: clamp01(height, 1 - y),
  };
}

function clamp01(v: number, max = 1): number {
  return Math.min(Math.max(v, 0), max);
}
